
import { toast } from "sonner";
import { User, UserRole } from "@/types";
import { 
  getUsers, 
  saveUsers, 
  storeUserPassword, 
  removeUserPassword,
  storeUserSession
} from "@/services/UserService";
import { v4 as uuidv4 } from "uuid";

export const useUserManagement = (user: User | null, setUser: (user: User | null) => void) => {
  // Registrar um novo usuário (apenas admins podem fazer isso)
  const register = async (
    nome: string, 
    email: string, 
    password: string, 
    role: UserRole,
    permissions?: Record<string, boolean>
  ) => {
    try {
      const users = getUsers();
      
      // Verificar se já existe um usuário com este email
      if (users.some(u => u.email.toLowerCase() === email.toLowerCase())) {
        toast.error("Já existe um usuário com este e-mail");
        return false;
      }
      
      const newUser = {
        id: uuidv4(),
        nome,
        email,
        role,
        permissions
      };
      
      users.push(newUser);
      saveUsers(users);
      
      // Salvar senha separadamente
      storeUserPassword(newUser.id, password);
      
      toast.success("Usuário criado com sucesso");
      return true;
    } catch (error) {
      console.error(error);
      toast.error("Erro ao criar usuário");
      return false;
    }
  };

  // Atualizar dados de um usuário
  const updateUser = async (userId: string, data: Partial<User>) => {
    try {
      const users = getUsers();
      const index = users.findIndex(u => u.id === userId);
      
      if (index === -1) {
        toast.error("Usuário não encontrado");
        return false;
      }
      
      // Se estiver atualizando o email, verificar se já existe
      if (data.email && data.email !== users[index].email) {
        if (users.some(u => u.email.toLowerCase() === data.email?.toLowerCase())) {
          toast.error("Já existe um usuário com este e-mail");
          return false;
        }
      }
      
      users[index] = { ...users[index], ...data };
      saveUsers(users);
      
      // Se for o usuário logado, atualizar o estado
      if (user && user.id === userId) {
        setUser(users[index]);
        storeUserSession(users[index]);
      }
      
      toast.success("Usuário atualizado com sucesso");
      return true;
    } catch (error) {
      console.error(error);
      toast.error("Erro ao atualizar usuário");
      return false;
    }
  };

  // Iniciar processo de recuperação de senha
  const resetPassword = async (email: string) => {
    try {
      const users = getUsers();
      const index = users.findIndex(u => u.email.toLowerCase() === email.toLowerCase());
      
      if (index === -1) {
        // Não informar ao usuário que o email não existe por segurança
        toast.success("Se o e-mail existir, você receberá instruções para recuperar sua senha");
        return false;
      }
      
      const resetToken = Math.random().toString(36).substring(2, 15);
      const now = new Date();
      const resetTokenExpiry = now.getTime() + 3600000; // Token válido por 1 hora
      
      // Em uma aplicação real, enviaríamos um email com o link de recuperação
      // Aqui apenas simulamos o processo
      
      toast.success("Se o e-mail existir, você receberá instruções para recuperar sua senha");
      
      // Salvar o token no usuário
      users[index].resetToken = resetToken;
      users[index].resetTokenExpiry = resetTokenExpiry;
      saveUsers(users);
      
      return true;
    } catch (error) {
      console.error(error);
      toast.error("Erro ao processar solicitação");
      return false;
    }
  };

  // Alterar senha de um usuário (admin)
  const changePassword = async (userId: string, newPassword: string) => {
    try {
      const users = getUsers();
      const index = users.findIndex(u => u.id === userId);
      
      if (index === -1) {
        toast.error("Usuário não encontrado");
        return false;
      }
      
      // Salvar nova senha
      storeUserPassword(userId, newPassword);
      
      // Limpar tokens de recuperação se existirem
      if (users[index].resetToken) {
        delete users[index].resetToken;
        delete users[index].resetTokenExpiry;
        saveUsers(users);
      }
      
      toast.success("Senha alterada com sucesso");
      return true;
    } catch (error) {
      console.error(error);
      toast.error("Erro ao alterar senha");
      return false;
    }
  };

  // Excluir usuário
  const deleteUser = async (userId: string) => {
    try {
      // Não permitir excluir a si mesmo
      if (user && user.id === userId) {
        toast.error("Você não pode excluir sua própria conta");
        return false;
      }
      
      const users = getUsers();
      const newUsers = users.filter(u => u.id !== userId);
      
      if (newUsers.length === users.length) {
        toast.error("Usuário não encontrado");
        return false;
      }
      
      saveUsers(newUsers);
      removeUserPassword(userId);
      
      toast.success("Usuário excluído com sucesso");
      return true;
    } catch (error) {
      console.error(error);
      toast.error("Erro ao excluir usuário");
      return false;
    }
  };
  
  // Obter todos os usuários
  const getAllUsers = () => {
    return getUsers();
  };

  return {
    register,
    updateUser,
    resetPassword,
    changePassword,
    deleteUser,
    getAllUsers
  };
};
